<template>
  <div>
    <div
      v-if="retrieving"
      class="d-flex justify-content-center align-items-center"
    >
      <div
        class="spinner-grow text-info"
        style="width: 8rem; height: 8rem;"
        role="status"
      ></div>
    </div>
    <div v-else class="row parent-container">
      <!-- verification status -->
      <div v-if="!isVerified">
        <verifier :data="certificate" @finished="verificationFinished" />
      </div>

      <div
        v-if="isVerified"
        class="PrintActionWrapper"
        v-bind:style="{ background: headerColor }"
      >
        <button
          @click="generateReport"
          class="btn btn-secondary PrintButton mr-3"
        >
          Download as PDF
        </button>
        <div class="badge">
          <img
            v-bind:src="emblemImage"
            alt="Badge of a Valid Certificate"
            height="80%"
            width="80%"
          />
        </div>
        <div class="d-inline-block position-relative">
          <button @click="printCertJSON" class="btn btn-secondary PrintButton">
            Print as JSON
          </button>

          <div class="how-does-it-work-container">
            <router-link
              to="/how-does-it-work"
              target="_blank"
              class="text-white"
              >How does it work?</router-link
            >
          </div>
        </div>
      </div>
      <!-- certificate -->
      <div class="ParentWrapper">
        <div
          id="CertificateContainer"
          class="PaperContainer html2pdf__page-break"
        >
          <div 
            v-if="!isVerified"
            id="blur-container"
            :style="{
                backgroundImage: `url(${getBlurImage(provider)})`
              }"
          >
            <div v-if="!certificate" id="no-certificate">
              Certificate Not Found
            </div>
          </div>
          <div
            v-else
            id="CertificateRelativeContainer"
            v-bind:style="{ backgroundImage: bgImage }"
          >
            <div id="LogoContainer">
              <img
                v-bind:src="logoImage"
                alt="Logo Image"
                class="certificate-img"
              />
            </div>

            <div id="CourseTitleContainer">{{ courseName }}</div>

            <div v-show="certificate" id="AcknowledgementContainer">
              <p>This Certificate of Completion is to acknowledge that</p>
            </div>

            <div
              v-show="certificate"
              id="AwardeeContainer"
              class="text-uppercase"
            >
              {{ fname }} {{ lname }}
            </div>

            <div id="QuoteContainer" class="d-inline-block">
              <p v-show="certificate">
                has successfully completed the {{ courseName }} <br />training
                program offered by {{ provider }}
                <br />
                on {{ getIssueDate() }}
              </p>
            </div>

            <div v-show="certificate" class="d-flex" id="SignatoriesContainer">
              <div
                v-for="signatory in getSignatories()"
                :key="signatory.fullname"
                class="signatory"
              >
                <div v-if="signatory" class="img-signature">
                  <img
                    :src="getSignatureSrc(signatory)"
                    class="signatureImage"
                  />
                </div>
                <div v-if="signatory" id="signature-line" />
                <div v-if="signatory">
                  <b>{{ signatory.fullname || '' }}</b>
                  <p class="mb-0">{{ signatory.designation || '' }}</p>
                  <p class="mb-1">{{ signatory.institution || '' }}</p>
                </div>
              </div>
            </div>

            <!-- footer -->
            <div
              id="UrlContainer"
              v-if="certificate"
              class="px-2 py-1 text-center justify-content-center"
            >
              <div>
                <div
                  class="d-flex justify-content-center align-items-center px-4"
                >
                  <b>
                    This Certificate is on the Blockchain, to verify go to:
                    <br />
                    <a :href="verifyUrl">{{ verifyUrl }}</a>
                    <hr class="my-2" :style="{ border: '0' }"/>
                    Kingsland University is accredited by SACS-CASI. To verify, go to:
                    <br/>
                    <a
                      href="https://home.cognia.org/p/registry"
                      style="color: #2586f6; font-weight: 600"
                    >
                      https://home.cognia.org/p/registry
                    </a>
                  </b>
                  <div id="QRBig" class="QRCode">
                    <qrcode-vue
                      :value="verifyUrl"
                      level="L"
                      size="90"
                    ></qrcode-vue>
                  </div>
                  <div id="QRMed" class="QRCode">
                    <qrcode-vue
                      :value="verifyUrl"
                      level="L"
                      size="60"
                    ></qrcode-vue>
                  </div>
                  <div id="QRSmall" class="QRCode">
                    <qrcode-vue
                      :value="verifyUrl"
                      level="L"
                      size="35"
                    ></qrcode-vue>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Certificate Modules -->
        <div
          v-if="isVerified && modules.length"
          id="ModulesContainer"
          class="PaperContainer html2pdf__page-break"
        >
          <div
            id="CertificateRelativeContainer"
            v-bind:style="{ backgroundImage: bgImage }"
          >
            <div id="CourseTitleContainer">Course Curriculum</div>
            <div id="ModuleSetContainer">
              <div class="d-flex flex-wrap justify-content-center w-100 mr-3">
                <div
                  v-for="module in modules"
                  v-bind:key="module.displayName"
                  v-bind:class="{
                    'col-10': totalTopics <= 30,
                    'ModuleGroup col-6': totalTopics > 30,
                  }"
                >
                  <table class="table table-borderless">
                    <thead>
                      <tr>
                        <th class="p-0" colspan="1">
                          <div
                            class="text-left"
                            :style="dynamicStyle(30 / getModulesLength())"
                          >
                            <u>{{ module.displayName }}</u>
                          </div>
                        </th>
                        <th class="p-0" colspan="1">
                          <div :style="dynamicStyle(30 / getModulesLength())">
                            <u>Duration</u>
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody class="w-100">
                      <tr
                        v-for="topic in module.topics"
                        v-bind:key="topic.name"
                      >
                        <td
                          class="text-left w-100"
                          v-bind:class="{ 'pl-4': topic.hours === 0 }"
                          :style="dynamicStyle(15 / getModulesLength())"
                        >
                          {{ topic.name }}
                        </td>
                        <td
                          class="text-center w-100"
                          :style="dynamicStyle(15 / getModulesLength())"
                        >
                          {{ calculateDuration(topic.hours) }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <!-- footer -->
            <div
              id="UrlContainer"
              v-if="certificate"
              class="px-2 py-1 text-center justify-content-center"
            >
              <div>
                <div
                  class="d-flex justify-content-center align-items-center px-4"
                >
                  <b>
                    This Certificate is on the Blockchain, to verify go to:
                    <br />
                    <a :href="verifyUrl">{{ verifyUrl }}</a>
                    <hr class="my-2" :style="{ border: '0' }"/>
                    Kingsland University is accredited by SACS-CASI. To verify,
                    go to:
                    <br />
                    <a
                      href="https://home.cognia.org/p/registry"
                      style="color: #2586f6; font-weight: 600"
                    >
                      https://home.cognia.org/p/registry
                    </a>
                  </b>
                  <div id="QRBig" class="QRCode">
                    <qrcode-vue
                      :value="verifyUrl"
                      level="L"
                      size="90"
                    ></qrcode-vue>
                  </div>
                  <div id="QRMed" class="QRCode">
                    <qrcode-vue
                      :value="verifyUrl"
                      level="L"
                      size="60"
                    ></qrcode-vue>
                  </div>
                  <div id="QRSmall" class="QRCode">
                    <qrcode-vue
                      :value="verifyUrl"
                      level="L"
                      size="35"
                    ></qrcode-vue>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Transfer contents to different file -->
      <vue-html2pdf
        :show-layout="false"
        :float-layout="true"
        :enable-download="true"
        :preview-modal="windowWidth > 1200"
        :manual-pagination="true"
        :htmlToPdfOptions="{
          image: {
            type: 'png',
            quality: 2,
          },
          filename: getPDFName(),
          enableLinks: true,
          jsPDF: {
            unit: 'in',
            format: 'a4',
            orientation: 'portrait',
          },
        }"
        pdf-content-width="800px"
        @progress="onProgress($event)"
        @hasStartedGeneration="hasStartedGeneration()"
        @hasGenerated="hasGenerated($event)"
        ref="html2Pdf"
      >
        <section slot="pdf-content">
          <!-- certificate -->
          <div
            id="CertificateContainer"
            class="PaperContainerPDF html2pdf__page-break"
          >
            <div
              id="CertificateRelativeContainer"
              v-bind:style="{ backgroundImage: bgImage }"
            >
              <div id="LogoContainerPDF">
                <img
                  v-bind:src="logoImage"
                  alt="Logo Image"
                  class="certificate-img"
                />
              </div>

              <div id="CourseTitleContainerPDF">{{ courseName }}</div>

              <div v-show="certificate" id="AcknowledgementContainerPDF">
                <p>This Certificate of Completion is to acknowledge that</p>
              </div>

              <div
                v-show="certificate"
                id="AwardeeContainerPDF"
                class="text-uppercase"
              >
                {{ fname }} {{ lname }}
              </div>

              <div id="QuoteContainerPDF" class="d-inline-block">
                <p v-show="certificate">
                  has successfully completed the {{ courseName }} <br />training
                  program offered by {{ provider }}
                  <br />
                  on {{ getIssueDate() }}
                </p>
              </div>

              <div
                v-show="certificate"
                class="d-flex"
                id="SignatoriesContainerPDF"
              >
                <div
                  v-for="signatory in getSignatories()"
                  :key="signatory.fullname"
                  class="signatory"
                >
                  <div v-if="signatory" class="img-signature">
                    <img
                      :src="getSignatureSrc(signatory)"
                      class="signatureImage"
                    />
                  </div>
                  <div v-if="signatory" id="signature-line" />
                  <div v-if="signatory">
                    <b>{{ signatory.fullname || '' }}</b>
                    <p class="mb-0">{{ signatory.designation || '' }}</p>
                    <p class="mb-1">{{ signatory.institution || '' }}</p>
                  </div>
                </div>
              </div>

              <!-- footer -->
              <div
                id="UrlContainerPDF"
                v-if="certificate"
                class="px-2 py-1 text-center justify-content-center"
              >
                <div>
                  <div
                    class="d-flex justify-content-center align-items-center px-4"
                  >
                    <b>
                      This Certificate is on the Blockchain, to verify go to:
                      <br />
                      <a :href="verifyUrl">{{ verifyUrl }}</a>
                      <hr class="my-2" :style="{ border: '0' }"/>
                      Kingsland University is accredited by SACS-CASI. To
                      verify, go to:
                      <br />
                      <a
                        href="https://home.cognia.org/p/registry"
                        style="color: #2586f6; font-weight: 600"
                      >
                        https://home.cognia.org/p/registry
                      </a>
                    </b>
                    <div id="QRBig" class="QRCode">
                      <qrcode-vue
                        :value="verifyUrl"
                        level="L"
                        size="90"
                      ></qrcode-vue>
                    </div>
                    <div id="QRMed" class="QRCode">
                      <qrcode-vue
                        :value="verifyUrl"
                        level="L"
                        size="60"
                      ></qrcode-vue>
                    </div>
                    <div id="QRSmall" class="QRCode">
                      <qrcode-vue
                        :value="verifyUrl"
                        level="L"
                        size="35"
                      ></qrcode-vue>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Certificate Modules -->
          <div
            v-if="isVerified && modules.length"
            id="ModulesContainer"
            class="PaperContainerPDF html2pdf__page-break"
          >
            <div
              id="CertificateRelativeContainer"
              v-bind:style="{ backgroundImage: bgImage }"
            >
              <div id="CourseTitleContainerPDF">Course Curriculum</div>
              <div id="ModuleSetContainer">
                <div class="d-flex flex-wrap justify-content-center w-100 mr-3">
                  <div
                    v-for="module in modules"
                    v-bind:key="module.displayName"
                    v-bind:class="{
                      'col-10': totalTopics <= 30,
                      'ModuleGroupPDF col-6': totalTopics > 30,
                    }"
                  >
                    <table class="table table-borderless">
                      <thead>
                        <tr>
                          <th class="p-0" colspan="1">
                            <div
                              class="text-left"
                              :style="
                                dynamicStyle(30 / getModulesLength(), true)
                              "
                            >
                              <u>{{ module.displayName }}</u>
                            </div>
                          </th>
                          <th class="p-0" colspan="1">
                            <div
                              :style="
                                dynamicStyle(30 / getModulesLength(), true)
                              "
                            >
                              <u>Duration</u>
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody class="w-100">
                        <tr
                          v-for="topic in module.topics"
                          v-bind:key="topic.name"
                        >
                          <td
                            class="text-left w-100"
                            v-bind:class="{ 'pl-4': topic.hours === 0 }"
                            :style="dynamicStyle(15 / getModulesLength(), true)"
                          >
                            {{ topic.name }}
                          </td>
                          <td
                            class="text-center w-100"
                            :style="dynamicStyle(15 / getModulesLength(), true)"
                          >
                            {{ calculateDuration(topic.hours) }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <!-- footer -->
              <div
                id="UrlContainerPDF"
                v-if="certificate"
                class="px-2 py-1 text-center justify-content-center"
              >
                <div>
                  <div
                    class="d-flex justify-content-center align-items-center px-4"
                  >
                    <b>
                      This Certificate is on the Blockchain, to verify go to:
                      <br />
                      <a :href="verifyUrl">{{ verifyUrl }}</a>
                      <hr class="my-2" :style="{ border: '0' }"/>
                      Kingsland University is accredited by SACS-CASI. To
                      verify, go to:
                      <br />
                      <a
                        href="https://home.cognia.org/p/registry"
                        style="color: #2586f6; font-weight: 600"
                      >
                        https://home.cognia.org/p/registry
                      </a>
                    </b>
                    <div id="QRBig" class="QRCode">
                      <qrcode-vue
                        :value="verifyUrl"
                        level="L"
                        size="90"
                      ></qrcode-vue>
                    </div>
                    <div id="QRMed" class="QRCode">
                      <qrcode-vue
                        :value="verifyUrl"
                        level="L"
                        size="60"
                      ></qrcode-vue>
                    </div>
                    <div id="QRSmall" class="QRCode">
                      <qrcode-vue
                        :value="verifyUrl"
                        level="L"
                        size="35"
                      ></qrcode-vue>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- certificate -->
        </section>
      </vue-html2pdf>
      <!-- Transfer contents to different file -->
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import QrcodeVue from 'qrcode.vue';
import VueHtml2pdf from 'vue-html2pdf';
import Verifier from './verifier';
import certlib from '../../lib/Certificate';

export default {
  name: 'Certificate',
  components: {
    Verifier,
    QrcodeVue,
    VueHtml2pdf,
  },
  data() {
    return {
      retrieving: true,
      certificate: null,
      errMsg: null,
      showErr: false,
      windowWidth: null,
      email: null,
      id: null,
      fname: null,
      lname: null,
      courseName: null,
      modules: [],
      issuedAt: null,

      showVerification: false,
      isVerifying: false,
      isVerified: false,

      imgBaseUrl: process.env.VUE_APP_API_BASE_URL,

      bgImage: `url(${require('./../../assets/Cert-BG.jpg')})`,
      logoImage: require('./../../assets/Kingsland_University_wK_Dark_Vertical_resized.png'),
      emblemImage: require('./../../assets/Valid_Badge.png'),
      provider: 'Kingsland University',
      headerColor: '#211f47',
    };
  },
  created: async function() {
    let vm = this;
    try {
      let certificateId = vm.$route.params.id;
      let uploadedJsonString = window.localStorage.getItem('certificate');
      if (uploadedJsonString) {
        let uploadedJson = JSON.parse(uploadedJsonString);
        if (
          uploadedJson._id === certificateId ||
          (uploadedJson.signature || {}).payloadHash === certificateId
        ) {
          vm.certificate = uploadedJson;
          vm.populateData(uploadedJson);
          vm.retrieving = false;
          return;
        }
      }

      let certificate = await certlib.getCertificateById(certificateId);

      if (certificate) {
        vm.certificate = certificate;
        vm.populateData(vm.certificate);
        vm.populateCustomData();
      } else vm.courseName = 'Certificate ID Not Found';
    } catch (error) {
      vm.courseName = error.message || 'An unknown error occurred.';
    }
    vm.retrieving = false;
  },
  mounted() {
    this.windowWidth = window.innerWidth;
    window.addEventListener('resize', () => {
      this.windowWidth = window.innerWidth;
    });
  },
  beforeDestroy() {
    window.removeEventListener('resize', () => {
      this.windowWidth = window.innerWidth;
    });
  },
  methods: {
    populateData: function(cert) {
      const { email, id, fname, lname } = cert.payload['@data'];
      this.id = id;
      this.email = email;
      this.fname = fname;
      this.lname = lname;
      this.courseName = cert.template.course;
      this.modules = [];
      if (cert.payload['@course'] && cert.payload['@course'].modules) {
        this.modules = cert.payload['@course'].modules;
      }
      const firstIssuerHash = Object.keys(cert.__commands);
      const issuedAtUnixFormat =
        cert.__commands[firstIssuerHash[0]][0].issuedAt;
      this.issuedAt = moment(issuedAtUnixFormat).format('Do of MMMM, YYYY');
    },
    populateCustomData: async function() {
      const { template } = this.certificate;

      if (template.customCertificateProvider) {
        this.provider = template.customCertificateProvider;
      }

      if (template.customHeaderColor) {
        this.headerColor = template.customHeaderColor;
      }

      if (template.customBg) {
        this.bgImage = template.customBg.bgBase64
          ? `url(${template.customBg.bgBase64})`
          : `url(${await this.convertUrlToBase64(
              `${this.imgBaseUrl}${template.customBg.bgLink}`
            )})`;
      }

      if (template.customLogo) {
        this.logoImage = template.customLogo.logoBase64
          ? template.customLogo.logoBase64
          : await this.convertUrlToBase64(
              `${this.imgBaseUrl}${template.customLogo.logoLink}`
            );
      }

      if (template.customEmblem) {
        this.emblemImage = template.customEmblem.emblemBase64
          ? template.customEmblem.emblemBase64
          : await this.convertUrlToBase64(
              `${this.imgBaseUrl}${template.customEmblem.emblemLink}`
            );
      }

      template.signatories.forEach(async (signatory, i) => {
        if (!signatory.signatureBase64) {
          this.certificate.template.signatories[
            i
          ].signatureBase64 = await this.convertUrlToBase64(
            `${this.imgBaseUrl}${signatory.signatureLink}`
          );
        }
      });
    },
    getSignatories: function() {
      const certificate = this.certificate;
      const { payload } = certificate;
      const version = payload['@certificateVersion'];
      if (version && version === 2 && payload['@signatories']) {
        return payload['@signatories'];
      } else {
        return certificate.template.signatories;
      }
    },
    getIssueDate: function() {
      const { payload } = this.certificate;
      let issueDate = this.issuedAt;
      if (
        payload['@certificateVersion'] &&
        [1, 2, 3, 4].includes(payload['@certificateVersion'])
      ) {
        issueDate = moment(payload['@data'].issueDate).format(
          'Do of MMMM, YYYY'
        );
      }

      return issueDate;
    },
    getSignatureSrc: function(signatory) {
      return (
        signatory &&
        (signatory.signatureBase64 ||
          `${process.env.VUE_APP_API_BASE_URL}${signatory.signatureLink}`)
      );
    },
    convertUrlToBase64: async function(link) {
      const data = await fetch(link);
      const blob = await data.blob();
      return new Promise(resolve => {
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = () => {
          const base64data = reader.result;
          resolve(base64data);
        };
      });
    },
    printCertPDF: function() {
      window.print();
    },
    printCertJSON: async function() {
      var dataStr =
        'data:text/json;charset=utf-8,' +
        encodeURIComponent(JSON.stringify(this.certificate));
      var downloadAnchorNode = document.createElement('a');
      downloadAnchorNode.setAttribute('href', dataStr);
      downloadAnchorNode.setAttribute(
        'download',
        `${this.fname}-${this.lname}-Certificate.json`
      );
      document.body.appendChild(downloadAnchorNode); // required for firefox
      downloadAnchorNode.click();
      downloadAnchorNode.remove();
    },
    verifyCertificate: async function() {
      this.showVerification = true;
      this.isVerifying = true;
      this.isVerified = false;
      this.$refs.Verifier.startVerification();
    },
    verificationFinished: function(valid) {
      this.isVerifying = false;
      this.isVerified = valid;
    },
    getModulesLength: function() {
      if (this.modules.length === 1) {
        return 2;
      }

      return this.modules.length;
    },
    dynamicStyle: function(base = 0, isPdf = false) {
      if (this.windowWidth <= 1200 && !isPdf)
        return `font-size:${5.8 + base}px`;
      return `font-size: ${10 + base}px`;
    },
    calculateDuration: function(hours) {
      const { payload } = this.certificate;
      const templateType = payload['@template'].type

      if (hours === 0) return '';
      if (templateType.toLowerCase() !== 'asynchronous' && hours % 80 === 0) return `${hours / 40} weeks`;
      return `${hours} ${hours === 1 ? 'hr' : 'hrs'}`;
    },
    generateReport() {
      this.$refs.html2Pdf.generatePdf();
    },
    getPDFName() {
      return `${this.courseName} Certificate - (${this.fname} ${this.lname})`;
    },
    getBlurImage: function(provider) {
      if(provider === 'DevLeague') {
        return require('./../../assets/FrameDDL.png');
      }

      return require('./../../assets/Frame.png');
    }
  },
  computed: {
    verifyUrl() {
      let vm = this;
      const backendUrl = window.location.origin.toLowerCase();
      const certId = vm.$route.params.id.toLowerCase();
      return `${backendUrl}/certificate/${certId}`;
    },
    totalTopics() {
      let total = 0;
      this.modules.forEach(module => {
        total += module.topics.length + 1;
      });
      return total;
    },
  },
};
</script>

<style scoped>
* {
  font-family: 'Open Sans', sans-serif;
}

td {
  padding: 0;
  text-align: left;
}
.no-print {
  text-align: center;
  /*position: fixed;*/
  left: 2em;
  top: 5em;
  width: 25em;
  margin-left: 3rem;
}

#no-print-desc {
  text-align: left;
  font-family: 'Montserrat';
  font-weight: 300;
  font-size: 15px;
  padding-bottom: 2em;
}

@media print {
  .no-print {
    display: none;
  }
}

/* Remove Header and Footer while printing */
@page {
  size: A4;
  margin: 0mm;
}

#app {
  margin: 0;
  text-align: center;
  font-family: 'Verdana', Sans-serif;
}

#badgeStatus {
  position: absolute;
  top: -50px;
  z-index: 99999;
  right: 200px;
}

@media print {
  #badgeStatus {
    display: none;
  }
}

#PrintContainer {
  text-align: center;
  background-color: unset !important;
}

#CertificateRelativeContainer {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  /* background-image: url(./../../assets/Cert-BG.jpg); */
  background-repeat: no-repeat;
  /*background-size: cover;*/
  background-size: 100% 100%;
}

.ParentWrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  background-image: url(./../../assets/what-the-hex.png);
}

.PrintActionWrapper {
  width: 100%;
  height: 4rem;
  justify-content: center;
  align-items: center;
  display: flex;
  z-index: 11;
  top: 0;
  left: 0;
  right: 0;
  position: fixed;
}

.PrintButton {
  width: 150px;
}

.PaperContainer {
  border-style: solid;
  border-width: 1px;
  margin-top: 5em;
  margin-bottom: 5em;
  text-align: center;
  position: relative;
  overflow: hidden;
  width: 21cm;
  height: 29.7cm;
  z-index: 10;
}

/* These are styles for PDF mode. Transfer with the html too  */

.PaperContainerPDF {
  border-style: solid;
  text-align: center;
  position: relative;
  overflow: hidden;
  width: 21cm;
  height: 29.68cm;
}

#CourseTitleContainerPDF {
  margin-top: 25px;
  font-size: 40px;
  font-weight: 200;
  color: #1f1b4f;
}

#AcknowledgementContainerPDF {
  margin-top: 20px;
  font-size: 17px;
  letter-spacing: 1px;
}

#AwardeeContainerPDF {
  margin-top: 10px;
  margin-bottom: 10px;
  font-size: 40px;
  font-weight: bold;
}

.ModuleGroupPDF {
  padding-left: 2rem;
  padding-top: 1.5rem;
}

#LogoContainerPDF {
  margin-top: 70px;
  height: 320px;
  padding-left: 120px;
  padding-right: 120px;
  display: flex;
  justify-content: center;
  align-items: center;
}

/* .certificate-imgPDF {
  height: 300px;
  width: 410px;
} */

#QuoteContainerPDF {
  margin-top: 5px;
  width: 600px;
  font-size: 17px;
  letter-spacing: 1px;
  line-height: 25px;
}

#SignatoriesContainerPDF {
  position: relative;
  right: 0;
  left: 0;
  /* width: 100%; */
  display: flex;
  align-items: center;
  flex-direction: row;
  justify-content: space-evenly;
  flex-wrap: wrap;
  min-height: 320px;
}

#SignatoriesContainerPDF div hr {
  margin-left: 70px;
  /* width: 230px; */
}

#UrlContainerPDF {
  bottom: 60px;
  position: absolute;
  width: 100%;
  font-size: 0.8rem;
  margin-top: 35px;
}

/* These are styles for PDF mode. Transfer with the html too */

/*#CertificateContainer {
  .PaperContainer;
}

#ModulesContainer {
  .PaperContainer;
}*/

.verify {
  background-color: '#134260' !important;
}

@media print {
  #ModulesContainer {
    margin-top: 100vh;
  }
  .PaperContainer {
    page-break-after: always;
    position: absolute;
    left: 0em;
    margin-top: 0;
    margin-right: 0;
    margin-bottom: 0;
  }
}

.progress {
  width: 21cm;
}

.show-on-hover {
  position: absolute;
  color: gray;
  margin-top: 39px;
}

.certificate-img {
  max-height: 100%;
  max-width: 100%;
}

#blur-container {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-image: url(./../../assets/Frame.png);
  background-repeat: no-repeat;
  background-size: cover;
}

#LogoContainer {
  margin-top: 70px;
  height: 320px;
  padding-left: 120px;
  padding-right: 120px;
  display: flex;
  justify-content: center;
  align-items: center;
}

#CourseTitleContainer {
  margin-top: 30px;
  font-size: 40px;
  font-weight: 200;
  color: #1f1b4f;
}

#AcknowledgementContainer {
  margin-top: 25px;
  font-size: 17px;
  letter-spacing: 1px;
}

#AwardeeContainer {
  margin-top: 10px;
  margin-bottom: 10px;
  font-size: 40px;
  font-weight: bold;
}

#QuoteContainer {
  width: 100%;
  font-size: 17px;
  letter-spacing: 1px;
  line-height: 25px;
}

#no-certificate {
  position: absolute;
  height: 5em;
  top: 0px;
  background-color: #f1807d;
  width: 100%;
  justify-content: center;
  align-items: center;
  display: flex;
  color: white;
  font-size: 20px;
  font-family: 'Montserrat';
  font-weight: bold;
}
#SignatoriesContainer {
  position: relative;
  right: 0;
  left: 0;
  /* width: 100%; */
  display: flex;
  align-items: center;
  flex-direction: row;
  justify-content: space-evenly;
  flex-wrap: wrap;
  min-height: 320px;
}
#SignatoriesContainer div hr {
  margin-left: 70px;
  /* width: 230px; */
}
.img-signature {
  width: 150px;
  height: 60px;
  /* border-bottom: 1px solid #21252a */
}
.signatureImage {
  width: 100%;
  height: 100%;
}

#signature-line {
  border-top: 0.5px solid #e7e7ea;
  position: absolute;
  width: 120px;
  top: 50px;
  height: 1px;
}

.signatory {
  display: flex;
  margin: 0 3px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  position: relative;
}

#SignatoriesContainer img {
  /* bottom:60px;
  left:75px;
  width:60%; */
  /* min-width: 246px;
  max-width: 246px;
  min-height: 112px;
  max-height: 112px; */
}

.badge {
  height: 180px;
  width: 180px;
  margin-top: 115px;
}

#UrlContainer {
  bottom: 60px;
  position: absolute;
  width: 100%;
  font-size: 0.8rem;
  margin-top: 35px;
}

#ModuleSetContainer {
  height: 80%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.ModuleGroup {
  padding-left: 2rem;
  padding-top: 1.5rem;
}

.spinner-grow {
  margin-top: 40vh;
}

/* For QR code */

.QRCode {
  position: absolute;
  right: 2rem;
}

#QRBig {
  display: block;
}

#QRMed,
#QRSmall {
  display: none;
}

@media only screen and (max-width: 1200px) {
  .PaperContainer {
    width: 15cm;
    height: 20cm;
  }

  .ModuleGroup {
    padding-right: 2rem;
    padding-left: 2rem;
    padding-top: 0.5rem;
  }

  #LogoContainer {
    margin-top: 40px;
    padding-left: 60px;
    padding-right: 60px;
    height: 160px;
  }

  #CourseTitleContainer {
    font-size: 30px;
    margin-top: 30px;
  }

  #AcknowledgementContainer {
    margin-top: 25px;
    font-size: 14px;
  }

  #AwardeeContainer {
    font-size: 30px;
    /* margin-bottom: 12px;
    margin-top: 12px; */
  }

  #QuoteContainer {
    font-size: 14px;
  }

  #SignatoriesContainer {
    margin-top: -80px;
  }

  .signatory {
    font-size: 12px;
  }

  #UrlContainer {
    font-size: 10px;
    margin-top: 6px;
    bottom: 50px;
    position: absolute;
    width: 100%;
  }

  #UrlContainer {
    bottom: 30px;
  }

  #QRBig,
  #QRSmall {
    display: none;
  }

  #QRMed {
    display: block;
  }
}

.how-does-it-work-container {
  white-space: nowrap;
  position: absolute;
  left: 100%;
  top: 50%;
  transform: translateY(-50%) translateX(25px);
  font-size: 0.85em;
}

@media only screen and (max-width: 600px) {
  .ParentWrapper {
    display: block;
    background-image: none;
  }
}
</style>
